import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMessages } from 'contexts/MessagesContext';
import { useLeague } from 'contexts/LeagueContext';
import { useAuth } from 'contexts/AuthContext';
import Icon from 'components/AppIcon';

const ConversationPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagueMembers } = useLeague();
  const { getConversation, sendMessage, markAsRead } = useMessages();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const partner = leagueMembers.find((m) => m.userId === userId);
  const partnerName = partner?.name || 'Unknown User';

  const loadMessages = useCallback(async () => {
    setLoading(true);
    const msgs = await getConversation(userId);
    setMessages(msgs);
    setLoading(false);
    // Mark messages from this user as read
    await markAsRead(userId);
  }, [userId, getConversation, markAsRead]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const msgs = await getConversation(userId);
      setMessages(msgs);
      await markAsRead(userId);
    }, 5000);
    return () => clearInterval(interval);
  }, [userId, getConversation, markAsRead]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await sendMessage(userId, newMessage);
      setNewMessage('');
      // Reload messages
      const msgs = await getConversation(userId);
      setMessages(msgs);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  const ROLE_CONFIG = {
    owner: { label: 'Owner', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30' },
    admin: { label: 'Admin', color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/30' },
    member: { label: 'Member', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30' },
  };
  const partnerRole = partner?.role || 'member';
  const roleConfig = ROLE_CONFIG[partnerRole];

  return (
    <div className="flex flex-col" style={{ backgroundColor: 'var(--color-background)', height: '100dvh' }}>
      {/* Header */}
      <header
        className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0"
        style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
      >
        <button
          onClick={() => navigate('/messages')}
          className="p-2 rounded-lg hover:opacity-80 transition-opacity"
          style={{ backgroundColor: 'var(--color-muted)' }}
        >
          <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
        </button>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-muted)' }}>
          <Icon name="User" size={16} color="var(--color-muted-foreground)" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
            {partnerName}
          </p>
          <span className={`inline-block px-1.5 py-0.5 rounded-full font-caption text-xs font-semibold ${roleConfig.color}`}>
            {roleConfig.label}
          </span>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="MessageSquare" size={32} color="var(--color-muted-foreground)" />
            <p className="font-caption text-sm mt-2" style={{ color: 'var(--color-muted-foreground)' }}>
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date}>
              {/* Date divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }} />
                <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                  {formatDate(msgs[0].created_at)}
                </span>
                <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }} />
              </div>

              {/* Messages */}
              {msgs.map((msg) => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex mb-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-2xl ${
                        isMine ? 'rounded-br-md' : 'rounded-bl-md'
                      }`}
                      style={{
                        backgroundColor: isMine ? 'var(--color-primary)' : 'var(--color-muted)',
                        color: isMine ? '#FFFFFF' : 'var(--color-foreground)',
                      }}
                    >
                      <p className="font-body text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      <p
                        className="font-caption text-xs mt-1 text-right"
                        style={{ opacity: 0.7 }}
                      >
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 px-4 py-3 border-t flex-shrink-0"
        style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
      >
        <input
          ref={inputRef}
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e?.target?.value)}
          placeholder="Type a message..."
          autoCapitalize="sentences"
          autoComplete="off"
          className="flex-1 h-11 px-4 rounded-full border font-body text-sm"
          style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
          autoFocus
        />
        <button
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-40 flex-shrink-0"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          <Icon name="Send" size={18} color="#FFFFFF" />
        </button>
      </form>
    </div>
  );
};

export default ConversationPage;
